import { Box, Button, Typography, useTheme } from '@mui/material';

interface MagicLinkCheckEmailProps {
  email: string;
  /** When the server adds the user to the waitlist instead of sending a link. */
  variant?: 'check_email' | 'waitlist';
  onBack: () => void;
}

const MagicLinkCheckEmail = ({
  email,
  variant = 'check_email',
  onBack
}: MagicLinkCheckEmailProps) => {
  const theme = useTheme();
  const isWaitlist = variant === 'waitlist';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
        maxWidth: '400px',
        padding: '2rem'
      }}
    >
      <Typography
        sx={{
          marginBottom: '12px',
          fontWeight: 600,
          lineHeight: '24px',
          fontSize: '18px',
          textAlign: 'center'
        }}
      >
        {isWaitlist ? "You're on the waitlist" : 'Check your email'}
      </Typography>
      {isWaitlist ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          You&apos;ve been added to the waitlist for{' '}
          <Typography component="span" fontWeight={600} color="text.primary">
            {email}
          </Typography>
          . The Dokai team will reach out when it&apos;s your turn.
        </Typography>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We sent a sign-in link to{' '}
            <Typography component="span" fontWeight={600} color="text.primary">
              {email}
            </Typography>
            . Use it to finish signing in or creating your account.
          </Typography>
          <Typography variant="subtitle2" component="p" sx={{ m: 0, mb: 1 }}>
            Steps to log in
          </Typography>
          <Box
            component="ol"
            sx={{
              m: 0,
              mb: 3,
              pl: 2.5,
              '& li': { mb: 1 },
              '& li:last-of-type': { mb: 0 }
            }}
          >
            <Typography component="li" variant="body2">
              Open the inbox for that email address.
            </Typography>
            <Typography component="li" variant="body2">
              Find the message from Dokai and open it.
            </Typography>
            <Typography component="li" variant="body2">
              Click the sign-in link in the email. Your browser will open and
              complete login.
            </Typography>
          </Box>
        </>
      )}
      <Button
        variant="contained"
        sx={{
          width: '100%',
          height: '40px',
          borderRadius: '20px',
          textTransform: 'none',
          border: `1px solid ${theme.customColors.themePrimary}`,
          backgroundColor: theme.customColors.themeSecondary,
          color: theme.customColors.themePrimary,
          fontSize: '14px',
          lineHeight: '24px',
          boxShadow: 'none',
          transition: 'all 0.3s ease',
          ':hover': {
            border: `1px solid ${theme.customColors.themePrimary}`,
            backgroundColor: theme.customColors.themeSecondary,
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
          },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={onBack}
      >
        Back
      </Button>
    </Box>
  );
};

export default MagicLinkCheckEmail;
